// post-result.ts — (tf3-28; creative C1.6b handoff §3, AL-A-6) a Post that failed part-way: the backend answers
// { ok:false, code:"partly_posted", postIds, reason, message } — the posts in postIds DID go out. The page says so, and holds
// the Post button for that same selection: pressing it again would post those channels a second time (a retry is a new post
// by design).
// (tf3-33) + `targets`: the channels that got their post (only this Post's), and `targetsError` when they couldn't be read
import { w } from "@/lib/words.ts";   // (B107) the customer's lines, editable in Admin → Words

export interface PartlyPosted { postIds: string[]; reason: string; message: string; targets: { channelId: string; channel: string }[]; targetsError: string | null }
export function partlyPosted(d: unknown): PartlyPosted | null {
  const r = d as { ok?: unknown; code?: unknown; postIds?: unknown; reason?: unknown; message?: unknown; targets?: unknown; targetsError?: unknown } | null | undefined;
  if (!r || r.ok !== false || r.code !== "partly_posted") return null;
  const postIds = Array.isArray(r.postIds) ? r.postIds.filter((x): x is string => typeof x === "string") : [];
  const reason = typeof r.reason === "string" ? r.reason : w("page.post.no_reason");
  const targets = Array.isArray(r.targets) ? r.targets.filter((t): t is { channelId: string; channel: string } =>
    !!t && typeof (t as { channelId?: unknown }).channelId === "string" && typeof (t as { channel?: unknown }).channel === "string")
    .map((t) => ({ channelId: t.channelId, channel: t.channel })) : [];
  return { postIds, reason, message: typeof r.message === "string" ? r.message : w("page.post.partly", { n: postIds.length, reason }),
    targets, targetsError: typeof r.targetsError === "string" ? r.targetsError : null };
}
/** (tf3-33) which of the channels SENT got their post, and which didn't, by the account's name; null with no targets
 *  (they couldn't be read, or an older backend): the backend's message is shown instead. */
export function partlySplit(p: PartlyPosted, sentChannelIds: string[], nameOf: (channelId: string) => string):
    { went: string[]; didnt: string[] } | null {
  if (!p.targets.length) return null;
  const got = new Set(p.targets.map((t) => t.channelId));
  return { went: sentChannelIds.filter((id) => got.has(id)).map(nameOf), didnt: sentChannelIds.filter((id) => !got.has(id)).map(nameOf) };
}
/** The channels a Post was for, as one comparable key (order-free). */
export const selectionKey = (channelIds: string[]): string => [...channelIds].sort().join(",");

const PLACE: Record<string, string> = { tiktok: "TikTok", instagram_reels: "Instagram Reels", youtube_shorts: "YouTube Shorts",
  instagram: "Instagram", youtube: "YouTube", facebook: "Facebook" };
const place = (k: string) => PLACE[k] ?? k;
/** (tf3-29; C1.6b-2b) a skip's reason for a person. The two new ones (`<platform>_copy_refused: …`, `<channel>_no_copy: …`)
 *  lead with a code; they read as words here, their own detail kept. Any other reason is shown as it is. */
export function skipText(reason: string): string {
  let m = /^([a-z_]+)_copy_refused:\s*(.*)$/s.exec(reason);
  // (audit 10-16) contracts' copyRefusal starts "refused by publish_verify: " — said once
  if (m) return w("page.post.copy_refused", { platform: place(m[1]!), detail: m[2]!.replace(/^refused by publish_verify:\s*/, "") });
  m = /^([a-z_]+)_no_copy:\s*(.*)$/s.exec(reason);
  if (m) return w("page.post.no_copy", { platform: place(m[1]!), detail: m[2]! });
  // (audit 10-16) the workers' third copy skip (review AM S2-8): the required disclosure didn't fit this channel's post
  m = /^([a-z_]+)_disclosure_cut:\s*(.*)$/s.exec(reason);
  if (m) return `${place(m[1]!)}: ${m[2]}`;
  return reason;
}
